import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowLeft, IndianRupee, Calendar, Percent, TrendingUp, PieChart, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAnalytics } from '../../utils/useAnalytics';

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(10);
  const [tenure, setTenure] = useState(24);
  const [tenureType, setTenureType] = useState('months');
  const [result, setResult] = useState(null);
  const { trackView, trackGenerate, trackDownload } = useAnalytics('emi-calculator');

  // Track page view on mount
  useEffect(() => {
    trackView();
  }, [trackView]);

  const calculateEMI = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    const tenureMonths = tenureType === 'years' ? tenure * 12 : tenure;

    if (principal <= 0 || interestRate <= 0 || tenureMonths <= 0) {
      toast.error('Please enter valid values');
      return;
    }

    // EMI Formula: [P x R x (1+R)^N] / [(1+R)^N-1]
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths) / 
                (Math.pow(1 + monthlyRate, tenureMonths) - 1);
    
    const totalPayment = emi * tenureMonths;
    const totalInterest = totalPayment - principal;

    // Generate amortization schedule (first 12 months)
    const schedule = [];
    let balance = principal;
    for (let i = 1; i <= Math.min(tenureMonths, 12); i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;
      schedule.push({
        month: i,
        emi: Math.round(emi),
        principal: Math.round(principalPayment),
        interest: Math.round(interestPayment),
        balance: Math.max(0, Math.round(balance))
      });
    }

    setResult({
      emi: Math.round(emi),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
      principal,
      tenureMonths,
      schedule,
      interestPercentage: Math.round((totalInterest / totalPayment) * 100),
      principalPercentage: Math.round((principal / totalPayment) * 100)
    });

    // Track EMI calculation
    trackGenerate({ loanAmount: principal, interestRate, tenure: tenureMonths, emi: Math.round(emi) });
    toast.success('EMI calculated!');
  };

  const downloadSchedule = () => {
    if (!result) return;

    let csv = 'Month,EMI,Principal,Interest,Balance\n';
    let balance = loanAmount;
    const monthlyRate = interestRate / 12 / 100;
    
    for (let i = 1; i <= result.tenureMonths; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = result.emi - interestPayment;
      balance -= principalPayment;
      csv += `${i},${result.emi},${Math.round(principalPayment)},${Math.round(interestPayment)},${Math.max(0, Math.round(balance))}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emi-schedule-${loanAmount}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackDownload({ loanAmount, tenureMonths: result.tenureMonths });
    toast.success('Schedule downloaded!');
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  const presetAmounts = [100000, 500000, 1000000, 2500000, 5000000, 10000000];
  const presetRates = [7, 8.5, 10, 12, 15, 18];

  return (
    <div className="min-h-screen bg-background" data-testid="emi-calculator-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500/20 via-background to-emerald-500/10 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center">
              <Calculator className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">EMI Calculator</h1>
              <p className="text-muted-foreground">Calculate loan EMIs and payment schedules</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator */}
          <div className="glass-card p-6 space-y-6">
            {/* Loan Amount */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <IndianRupee className="w-4 h-4 text-emerald-500" />
                Loan Amount
              </label>
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-lg font-semibold"
                data-testid="loan-amount-input"
              />
              <input
                type="range"
                min="10000"
                max="50000000"
                step="10000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full mt-2 accent-emerald-500"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setLoanAmount(amount)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      loanAmount === amount 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {formatCurrency(amount)}
                  </button>
                ))}
              </div>
            </div>

            {/* Interest Rate */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <Percent className="w-4 h-4 text-yellow-500" />
                Interest Rate (% per year)
              </label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                step="0.1"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-lg font-semibold"
              />
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full mt-2 accent-yellow-500"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {presetRates.map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setInterestRate(rate)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      interestRate === rate 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>

            {/* Tenure */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <Calendar className="w-4 h-4 text-blue-500" />
                Loan Tenure
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-lg font-semibold"
                />
                <div className="flex rounded-xl overflow-hidden border border-white/10">
                  <button
                    onClick={() => setTenureType('months')}
                    className={`px-4 py-3 font-medium ${
                      tenureType === 'months' ? 'bg-blue-500 text-white' : 'bg-white/5'
                    }`}
                  >
                    Months
                  </button>
                  <button
                    onClick={() => setTenureType('years')}
                    className={`px-4 py-3 font-medium ${
                      tenureType === 'years' ? 'bg-blue-500 text-white' : 'bg-white/5'
                    }`}
                  >
                    Years
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[6, 12, 24, 36, 60, 120].map((t) => (
                  <button
                    key={t}
                    onClick={() => { setTenure(t); setTenureType('months'); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      tenure === t && tenureType === 'months'
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {t < 12 ? `${t}M` : `${t/12}Y`}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={calculateEMI}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="calculate-btn"
            >
              <Calculator className="w-5 h-5" />
              Calculate EMI
            </button>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {result ? (
              <>
                {/* EMI Result */}
                <div className="glass-card p-6 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-2 border-emerald-500/30">
                  <p className="text-sm text-muted-foreground mb-2">Monthly EMI</p>
                  <p className="text-4xl font-bold text-emerald-400" data-testid="emi-result">
                    ₹{result.emi.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    for {result.tenureMonths} months
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="glass-card p-4">
                    <p className="text-xs text-muted-foreground mb-1">Principal Amount</p>
                    <p className="text-xl font-bold text-blue-400">₹{result.principal.toLocaleString()}</p>
                  </div>
                  <div className="glass-card p-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Interest</p>
                    <p className="text-xl font-bold text-yellow-400">₹{result.totalInterest.toLocaleString()}</p>
                  </div>
                  <div className="glass-card p-4 col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Total Payment</p>
                    <p className="text-xl font-bold text-emerald-400">₹{result.totalPayment.toLocaleString()}</p>
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="glass-card p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-primary" />
                    Payment Breakdown
                  </h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1 h-6 rounded-full overflow-hidden bg-white/10 flex">
                      <div 
                        className="bg-blue-500 h-full"
                        style={{ width: `${result.principalPercentage}%` }}
                      ></div>
                      <div 
                        className="bg-yellow-500 h-full"
                        style={{ width: `${result.interestPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span>Principal ({result.principalPercentage}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span>Interest ({result.interestPercentage}%)</span>
                    </div>
                  </div>
                </div>

                {/* Amortization Preview */}
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Payment Schedule (First 12 Months)
                    </h3>
                    <button
                      onClick={downloadSchedule}
                      className="px-3 py-1.5 bg-white/10 rounded-lg text-sm flex items-center gap-1 hover:bg-white/20"
                    >
                      <Download className="w-4 h-4" />
                      Full CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="py-2 text-left text-muted-foreground">Month</th>
                          <th className="py-2 text-right text-muted-foreground">Principal</th>
                          <th className="py-2 text-right text-muted-foreground">Interest</th>
                          <th className="py-2 text-right text-muted-foreground">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.schedule.map((row) => (
                          <tr key={row.month} className="border-b border-white/5">
                            <td className="py-2">{row.month}</td>
                            <td className="py-2 text-right text-blue-400">₹{row.principal.toLocaleString()}</td>
                            <td className="py-2 text-right text-yellow-400">₹{row.interest.toLocaleString()}</td>
                            <td className="py-2 text-right">₹{row.balance.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="glass-card p-12 text-center">
                <Calculator className="w-20 h-20 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  Enter loan details and click calculate to see your EMI
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 glass-card p-6">
          <h3 className="font-semibold mb-4">Loan Tips</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-emerald-500 mb-2">Lower Interest = Less Payment</p>
              <p className="text-muted-foreground">Even 0.5% lower interest can save you lakhs over the loan tenure</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-blue-500 mb-2">Shorter Tenure = More Savings</p>
              <p className="text-muted-foreground">Higher EMI but significantly less total interest payment</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-yellow-500 mb-2">Prepayment Benefits</p>
              <p className="text-muted-foreground">Part prepayments reduce principal and total interest</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;
